import { gql } from "@/graphql/typed-gql"

/**
 * One change a grown-up can apply straight from a report. Kept apart from the
 * report's own fields because the two mutations that move it answer with an
 * action and nothing else — same fragment, so a button's state after pressing it
 * cannot differ from its state on load.
 */
export const REPORT_ACTION_FIELDS = gql`
  fragment ReportActionFields on ReportAction {
    id
    kind
    skillKey
    skillName
    label
    evidence
    effect
    rationale
    applied
    appliedAt
    appliedByRole
  }
`

/**
 * A report's prose and the numbers it was written from, kept apart the way the
 * server keeps them: `sections` is the AI's writing and is rendered as writing,
 * `stats` is the frozen snapshot and is where every figure on screen comes from.
 */
export const PROGRESS_REPORT_FIELDS = gql`
  fragment ProgressReportFields on ProgressReport {
    id
    audience
    subjectKey
    subjectName
    sections
    stats
    sessionCount
    coveredFrom
    coveredThrough
    requestedByRole
    createdAt
    # The buttons. Always empty on a learner's letter and for the student role —
    # the server decides that, not this selection. Their label, evidence and
    # effect are derived and stored on the row, so what a grown-up reads next to
    # a button is what the numbers said when it was offered.
    actions {
      ...ReportActionFields
    }
  }
  ${REPORT_ACTION_FIELDS}
`

/**
 * Everything a report screen needs: what has been written before, and how far off
 * the next one is. The availability half is what makes waiting legible — a learner
 * sees the meter fill instead of a button that refuses for reasons of its own.
 */
export const GET_PROGRESS_REPORTS = gql`
  query ProgressReports($studentId: ID!, $audience: String) {
    progressReports(studentId: $studentId, audience: $audience) {
      ...ProgressReportFields
    }
    progressReportAvailability(studentId: $studentId, audience: $audience) {
      allowed
      blockedBy
      reason
      requestedBy
      subjectKey
      subjectName
      sessionsDone
      sessionsNeeded
      sessionsLeft
      sessionProgress
      availableAt
      daysLeft
      lastReportAt
    }
  }
  ${PROGRESS_REPORT_FIELDS}
`

/**
 * The grown-up's view. One audience, not two: this page asks for the report
 * written *to the person reading it*, and the child's letter is theirs — it lives
 * on their own profile, arrives as an occasion, and is not a second thing for a
 * parent to commission from a tab.
 */
export const GET_STUDENT_PROGRESS_REPORTS = gql`
  query StudentProgressReports($studentId: ID!) {
    student(id: $studentId) {
      id
      name
      grade
    }
    guardianReports: progressReports(studentId: $studentId, audience: "guardian") {
      ...ProgressReportFields
    }
    guardianAvailability: progressReportAvailability(studentId: $studentId, audience: "guardian") {
      allowed
      blockedBy
      reason
      sessionsDone
      sessionsNeeded
      lastReportAt
    }
  }
  ${PROGRESS_REPORT_FIELDS}
`

/**
 * Just "is a letter waiting?", for a screen that is not the letters page — the
 * session finish screen asks it the moment the session is banked, and the
 * letters themselves would be a wasted round trip there.
 */
export const GET_PROGRESS_REPORT_AVAILABILITY = gql`
  query ProgressReportAvailabilityOnly($studentId: ID!) {
    progressReportAvailability(studentId: $studentId) {
      allowed
      subjectName
      sessionsDone
      sessionsNeeded
      sessionsLeft
      sessionProgress
      availableAt
      daysLeft
      lastReportAt
    }
  }
`
